import { take, uniqueId } from 'lodash';
import React from 'react';
import { View, ViewStyle, StyleProp } from 'react-native';
import {
  Svg,
  Circle,
  Rect,
  Defs,
  G,
  Text,
  ClipPath,
} from 'react-native-svg';
import * as API from 'generated/api';
import { Vec2, vec2 } from 'utility/gl-matrix';

interface Avatar$Props {
  user: API.MinimalUser;
  diameter: number;
  center: Vec2;
}

function userInitials(user: API.MinimalUser): string[] {
  const displayName = user.displayName;
  if (displayName == null) {
    return [];
  }

  return displayName
    .split(" ")
    .map(s => s.trim())
    .filter(s => s.length > 0)
    .map(s => s.charAt(0).toUpperCase());
}

const avatarColors = [
  "#F59A8B",
  "#F8B986",
  "#79D095",
  "#65CBCC",
  "#849FCC",
  "#CC9DC6"
];

// requires placement in Svg
export const Avatar = ({ user, center, diameter }: Avatar$Props) => {
  const initials = userInitials(user);
  /*
  // This is the logic from the iOS app. This method causes users with the same
  // initials to have the same color, which removes a lot of value from the
  // background color.
  const hashForBackgroundColor = initials.reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const fill = avatarColors[hashForBackgroundColor % avatarColors.length];
   */
  const fill = avatarColors[user.id % avatarColors.length];

  return (
    <BaseAvatar
      diameter={diameter}
      center={center}
      placeholderColor={fill}
      text={initials.length === 0 ? "?" : initials.join('')}
    />
  );
};

interface BaseAvatar$Props {
  text: string;
  placeholderColor: string;
  diameter: number;
  center: Vec2;
}

const BaseAvatar = ({ text, placeholderColor, diameter, center }: BaseAvatar$Props) => {
  const id = uniqueId('avatar');
  const maskID = `avatar.mask.${id}`;
  return (
    <>
      <Defs>
        <ClipPath
          id={maskID}
          {
            // DOM-only
            ...({clipPathUnits: "objectBoundingBox"} as any)
          }
        >
          <Circle
            r={0.5}
            cx={0.5}
            cy={0.5}
          />
        </ClipPath>
      </Defs>
      <G
        clipPath={`url(#${maskID})`}
      >
        <Rect
          x={vec2.x(center) - diameter / 2}
          y={vec2.y(center) - diameter / 2}
          width={diameter}
          height={diameter}
          fill={placeholderColor}
        >
        </Rect>
        <Text
          fill={'white'}
          x={vec2.x(center)}
          y={vec2.y(center)}
          textAnchor={"middle"}
          fontFamily="Caros"
          fontWeight="bold"
          fontSize={diameter * 0.38}
          dy={
            /* eyeballed; should use dominant-baseline but it is not implemented
             * in react-native-svg */
            diameter * 0.125
          }
        >
          {text}
        </Text>
      </G>
    </>
  );
};

interface Props {
  avatarDiameter: number;
  style: StyleProp<ViewStyle>;
  users: API.MinimalUser[];
}

// how many real avatars should we show before showing an overflow?
const OVERFLOW_LIMIT = 4;

const AvatarStack = (props: Props) => {
  const renderedDiameter = props.avatarDiameter;

  return (
    <View
      style={[
        {
          flexDirection: 'row-reverse',
          justifyContent: 'center',
          // backgroundColor: 'green',
        },
        props.style,
      ]}
    >
      {
        (props.users.length > OVERFLOW_LIMIT) && (
          <Svg
            key={"overflow"}
            width={props.avatarDiameter}
            height={props.avatarDiameter}
            viewBox={[0, 0, renderedDiameter, renderedDiameter].join(" ")}
            style={{
              marginLeft: -6,
            }}
          >
            <BaseAvatar
              text={`+${props.users.length - OVERFLOW_LIMIT}`}
              placeholderColor={'rgba(138, 167, 253, 1)'}
              center={vec2.scale(vec2.create(), vec2.unit(renderedDiameter), 0.5)}
              diameter={renderedDiameter}
            />
          </Svg>
        )
      }
      {
        take(props.users, OVERFLOW_LIMIT).map(user => (
          <Svg
            key={user.id}
            width={props.avatarDiameter}
            height={props.avatarDiameter}
            viewBox={[0, 0, renderedDiameter, renderedDiameter].join(" ")}
            style={{
              marginLeft: -6,
            }}
          >
            <Avatar
              user={user}
              center={vec2.scale(vec2.create(), vec2.unit(renderedDiameter), 0.5)}
              diameter={renderedDiameter}
            />
          </Svg>
        ))
      }
    </View>
  );
}

AvatarStack.defaultProps = {
  avatarDiameter: 30,
  style: {},
};

export default AvatarStack;
